import {
  Banknote,
  CalendarRange,
  Check,
  Clock,
  Landmark,
  X,
} from 'lucide-react';
import { animate } from 'motion';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { AMBULANCE_SERVICE_TYPES } from '@cold-ambulance/isomorphic';
import { Card, CardContent, cn } from '@cold-ambulance/shadcn';
import {
  type EquipmentItem,
  LocationLink,
  formatDate,
  formatTime,
} from '@cold-ambulance/ui';

type FormValues = {
  projectName: string;
  projectType: 'GOVERNMENT' | 'PRIVATE';
  location: { name: string; latitude: number; longitude: number };
  dates: { from: string; to: string };
  hours: { from: string; to: string };
  notes: string;
  mealsProvided: boolean;
  housingTransportProvided: boolean;
  isolatedWorkArea: boolean;
  ambulanceServiceWithDriver: Array<{
    type1Amount: number;
    type2Amount: number;
    type3Amount: number;
    type1Equipment: EquipmentItem[];
    type2Equipment: EquipmentItem[];
    type3Equipment: EquipmentItem[];
  }>;
  ambulanceServiceWithoutDriver: Array<{
    type1Amount: number;
    type2Amount: number;
    type3Amount: number;
    type1Equipment: EquipmentItem[];
    type2Equipment: EquipmentItem[];
    type3Equipment: EquipmentItem[];
  }>;
  ambulance4x4WithDriver: { amount: number; equipment: EquipmentItem[] };
  ambulance4x4WithoutDriver: { amount: number; equipment: EquipmentItem[] };
  mobileClinicBasic: { amount: number; equipment: EquipmentItem[] };
  mobileClinicAdvanced: { amount: number; equipment: EquipmentItem[] };
  paramedicServices: Array<{ amount: number; type: string }>;
  nurseServices: Array<{ amount: number; type: string }>;
  doctorServices: Array<{ specialization: string; amount: number }>;
  medicalEquipment: { name: string; quantity: number }[];
  proposalDeadline: string;
  bankGuaranteePercent: number;
  kpis: string[];
  paymentSchedule: {
    paymentType: 'MONTHLY' | 'DELIVERABLES';
    milestones: { name?: string; percentage: number }[];
  };
};

export type PreviewSectionId =
  | 'project'
  | 'medical'
  | 'booking'
  | 'contract'
  | 'additional';

export type PreviewOpenSections = {
  ambulanceServiceWithDriver: boolean;
  ambulanceServiceWithoutDriver: boolean;
  ambulance4x4WithDriver: boolean;
  ambulance4x4WithoutDriver: boolean;
  mobileClinicBasic: boolean;
  mobileClinicAdvanced: boolean;
  paramedicServices: boolean;
  nurseServices: boolean;
  doctorServices: boolean;
  medicalEquipment: boolean;
};

interface RequestPreviewProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  isOpen: boolean;
  onToggle: () => void;
  activeSection?: PreviewSectionId;
  openSections: PreviewOpenSections;
  requestType: 'SERVICE' | 'EQUIPMENT';
}

export function RequestPreview({
  form,
  isOpen,
  onToggle,
  activeSection,
  openSections,
  requestType,
}: RequestPreviewProps) {
  const { t } = useTranslation();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs = {
    project: React.useRef<HTMLDivElement>(null),
    medical: React.useRef<HTMLDivElement>(null),
    booking: React.useRef<HTMLDivElement>(null),
    contract: React.useRef<HTMLDivElement>(null),
    additional: React.useRef<HTMLDivElement>(null),
  };

  React.useEffect(() => {
    if (
      activeSection &&
      sectionRefs[activeSection]?.current &&
      scrollContainerRef.current
    ) {
      const container = scrollContainerRef.current;
      const section = sectionRefs[activeSection].current;
      const targetScroll = section.offsetTop - container.offsetTop - 16; // 16px padding

      animate(container.scrollTop, targetScroll, {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        onUpdate: (value) => {
          container.scrollTop = value;
        },
      });
    }
  }, [activeSection]);

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        type="button"
        onClick={onToggle}
        className="bg-primary text-primary-foreground fixed start-4 bottom-4 z-50 rounded-full px-4 py-3 font-medium shadow-lg lg:hidden"
      >
        {isOpen ? t('preview.hidePreview') : t('preview.showPreview')}
      </button>

      {/* Preview Content */}
      <div
        className={cn(
          'lg:sticky lg:bottom-48 lg:block lg:max-h-[calc(100vh-2rem)] lg:self-end',
          isOpen
            ? 'bg-background fixed inset-0 z-40 overflow-y-auto p-4'
            : 'hidden',
        )}
      >
        {/* Mobile Close Button */}
        {isOpen && (
          <button
            type="button"
            onClick={onToggle}
            className="text-primary mb-4 flex items-center gap-1 font-medium lg:hidden"
          >
            <X size={20} />
            {t('preview.hidePreview')}
          </button>
        )}

        <form.Subscribe
          selector={(state: { values: FormValues }) => state.values}
          children={(values: FormValues) => (
            <Card className="border-primary bg-[#E9F6ED]">
              <CardContent
                ref={scrollContainerRef}
                className="max-h-[calc(100vh-6rem)] space-y-4 overflow-y-auto py-6"
              >
                <h3 className="text-lg font-bold">{t('preview.title')}</h3>

                {/* Section 1: Service Details */}
                <PreviewSection
                  ref={sectionRefs.project}
                  title={t('preview.projectDetails')}
                  isActive={activeSection === 'project'}
                >
                  <PreviewField
                    label={t('preview.projectName')}
                    value={values.projectName}
                    placeholder={t('newRequest.notSpecified')}
                  />
                  <PreviewField
                    label={t('preview.projectEntity')}
                    value={
                      values.projectType === 'GOVERNMENT'
                        ? t('preview.government')
                        : t('preview.private')
                    }
                  />
                  {requestType === 'SERVICE' && (
                    <ServicesPreview
                      ambulanceWithDriver={
                        openSections.ambulanceServiceWithDriver
                          ? values.ambulanceServiceWithDriver
                          : []
                      }
                      ambulanceWithoutDriver={
                        openSections.ambulanceServiceWithoutDriver
                          ? values.ambulanceServiceWithoutDriver
                          : []
                      }
                      ambulance4x4WithDriver={
                        openSections.ambulance4x4WithDriver
                          ? values.ambulance4x4WithDriver
                          : { amount: 0, equipment: [] }
                      }
                      ambulance4x4WithoutDriver={
                        openSections.ambulance4x4WithoutDriver
                          ? values.ambulance4x4WithoutDriver
                          : { amount: 0, equipment: [] }
                      }
                      mobileClinicBasic={
                        openSections.mobileClinicBasic
                          ? values.mobileClinicBasic
                          : { amount: 0, equipment: [] }
                      }
                      mobileClinicAdvanced={
                        openSections.mobileClinicAdvanced
                          ? values.mobileClinicAdvanced
                          : { amount: 0, equipment: [] }
                      }
                    />
                  )}
                </PreviewSection>

                {/* Section 2: Medical Staff */}
                {requestType === 'SERVICE' && (
                  <PreviewSection
                    ref={sectionRefs.medical}
                    title={t('preview.medicalStaff')}
                    isActive={activeSection === 'medical'}
                  >
                    <MedicalStaffPreview
                      paramedicServices={
                        openSections.paramedicServices
                          ? values.paramedicServices
                          : []
                      }
                      nurseServices={
                        openSections.nurseServices ? values.nurseServices : []
                      }
                      doctorServices={
                        openSections.doctorServices ? values.doctorServices : []
                      }
                    />
                  </PreviewSection>
                )}

                {/* Section 2.5: Medical Equipment */}
                {requestType === 'EQUIPMENT' && (
                  <PreviewSection title={t('preview.medicalEquipment')}>
                    <MedicalEquipmentPreview
                      medicalEquipment={
                        openSections.medicalEquipment
                          ? values.medicalEquipment
                          : []
                      }
                    />
                  </PreviewSection>
                )}

                {/* Section 3: Booking Period & Location */}
                <PreviewSection
                  ref={sectionRefs.booking}
                  title={t('preview.bookingPeriodLocation')}
                  isActive={activeSection === 'booking'}
                >
                  <div className="flex items-center gap-2 text-sm">
                    <CalendarRange size={16} className="shrink-0" />
                    <span>
                      {values.dates.from && values.dates.to
                        ? `${formatDate(values.dates.from)} - ${formatDate(values.dates.to)}`
                        : t('newRequest.notSpecified')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={16} className="shrink-0" />
                    <span>
                      {values.hours.from && values.hours.to
                        ? `${formatTime(values.hours.from)} - ${formatTime(values.hours.to)}`
                        : t('newRequest.notSpecified')}
                    </span>
                  </div>
                  <LocationLink
                    name={values.location.name}
                    latitude={values.location.latitude}
                    longitude={values.location.longitude}
                    iconSize={16}
                    className="text-sm"
                  />
                </PreviewSection>

                {/* Section 4: Contract & Payment Terms */}
                <PreviewSection
                  ref={sectionRefs.contract}
                  title={t('preview.contractPaymentTerms')}
                  isActive={activeSection === 'contract'}
                >
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={16} className="shrink-0" />
                    <span className="text-muted-foreground">
                      {t('preview.proposalDeadline')}
                    </span>
                    <span>
                      {values.proposalDeadline
                        ? formatDate(values.proposalDeadline)
                        : t('newRequest.notSpecified')}
                    </span>
                  </div>
                  {values.bankGuaranteePercent > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Landmark size={16} className="shrink-0" />
                      <span className="text-muted-foreground">
                        {t('preview.bankGuarantee')}
                      </span>
                      <span>{values.bankGuaranteePercent}%</span>
                    </div>
                  )}
                  {values.paymentSchedule &&
                    values.paymentSchedule.milestones.length > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <Banknote size={16} className="shrink-0" />
                        <span className="text-muted-foreground">
                          {t('preview.paymentSystem')}
                        </span>
                        <span>
                          {t(
                            values.paymentSchedule.paymentType === 'MONTHLY'
                              ? 'newRequest.monthly'
                              : 'newRequest.deliverables',
                          )}
                        </span>
                      </div>
                    )}
                  {values.kpis.length > 0 && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">
                        {t('preview.kpis')}
                      </span>
                      <ul className="mt-1 list-inside list-disc text-xs">
                        {values.kpis.map((kpi, i) => (
                          <li key={i}>{kpi}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </PreviewSection>

                {/* Section 5: Additional Details */}
                <PreviewSection
                  ref={sectionRefs.additional}
                  title={t('preview.additionalDetails')}
                  isActive={activeSection === 'additional'}
                >
                  <BooleanPreviewField
                    label={t('preview.isolatedArea')}
                    value={values.isolatedWorkArea}
                  />
                  <BooleanPreviewField
                    label={t('preview.housingTransport')}
                    value={values.housingTransportProvided}
                  />
                  <BooleanPreviewField
                    label={t('preview.meals')}
                    value={values.mealsProvided}
                  />
                  {values.notes && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">
                        {t('preview.notes')}
                      </span>
                      <p className="mt-1 text-xs">{values.notes}</p>
                    </div>
                  )}
                </PreviewSection>
              </CardContent>
            </Card>
          )}
        />
      </div>
    </>
  );
}

const PreviewSection = React.forwardRef<
  HTMLDivElement,
  {
    title: string;
    children: React.ReactNode;
    isActive?: boolean;
  }
>(({ title, children, isActive }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'border-primary/30 space-y-2 border-b pb-4 transition-colors last:border-b-0 last:pb-0',
        isActive && 'bg-primary/10 -mx-2 rounded-lg px-2 py-2',
      )}
    >
      <h4 className="text-primary text-sm font-bold">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
});

function PreviewField({
  label,
  value,
  placeholder,
}: {
  label: string;
  value?: string | number;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const isEmpty = !value;
  const actualPlaceholder = placeholder || t('newRequest.notSpecified');
  const displayValue = value || actualPlaceholder;

  return (
    <div className="flex gap-x-2 text-sm">
      <span className="text-muted-foreground">{label}:</span>
      <span
        className={cn('text-end', isEmpty && 'text-muted-foreground italic')}
      >
        {displayValue}
      </span>
    </div>
  );
}

function BooleanPreviewField({
  label,
  value,
}: {
  label: string;
  value: boolean;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {value ? (
        <Check size={16} className="text-green-600" />
      ) : (
        <X size={16} className="text-red-500" />
      )}
      <span>{label}</span>
    </div>
  );
}

function ServicesPreview({
  ambulanceWithDriver,
  ambulanceWithoutDriver,
  ambulance4x4WithDriver,
  ambulance4x4WithoutDriver,
  mobileClinicBasic,
  mobileClinicAdvanced,
}: {
  ambulanceWithDriver: FormValues['ambulanceServiceWithDriver'];
  ambulanceWithoutDriver: FormValues['ambulanceServiceWithoutDriver'];
  ambulance4x4WithDriver: { amount: number; equipment: EquipmentItem[] };
  ambulance4x4WithoutDriver: { amount: number; equipment: EquipmentItem[] };
  mobileClinicBasic: { amount: number; equipment: EquipmentItem[] };
  mobileClinicAdvanced: { amount: number; equipment: EquipmentItem[] };
}) {
  // Filter services that have at least one non-zero amount
  const withDriverServices = ambulanceWithDriver.filter(
    (s) => s.type1Amount > 0 || s.type2Amount > 0 || s.type3Amount > 0,
  );
  const withoutDriverServices = ambulanceWithoutDriver.filter(
    (s) => s.type1Amount > 0 || s.type2Amount > 0 || s.type3Amount > 0,
  );

  const hasServices =
    withDriverServices.length > 0 ||
    withoutDriverServices.length > 0 ||
    ambulance4x4WithDriver.amount > 0 ||
    ambulance4x4WithoutDriver.amount > 0 ||
    mobileClinicBasic.amount > 0 ||
    mobileClinicAdvanced.amount > 0;

  const { t } = useTranslation();

  if (!hasServices) {
    return (
      <div className="text-muted-foreground text-sm italic">
        {t('newRequest.noServicesSelected')}
      </div>
    );
  }

  return (
    <div className="space-y-2 text-sm">
      {withDriverServices.map((service, index) => (
        <div key={`with-driver-${index}`}>
          <div className="font-medium">{t('services.ambulanceWithDriver')}</div>
          <div className="text-muted-foreground me-4 space-y-0.5 text-xs">
            {service.type1Amount > 0 && (
              <div>
                • {AMBULANCE_SERVICE_TYPES.type1.name}: {service.type1Amount}
                {service.type1Equipment.length > 0 && (
                  <div className="text-muted-foreground ms-3">
                    {t('preview.equipment')}{' '}
                    {service.type1Equipment
                      .map((e) => `${e.name} (${e.quantity})`)
                      .join('، ')}
                  </div>
                )}
              </div>
            )}
            {service.type2Amount > 0 && (
              <div>
                • {AMBULANCE_SERVICE_TYPES.type2.name}: {service.type2Amount}
                {service.type2Equipment.length > 0 && (
                  <div className="text-muted-foreground ms-3">
                    {t('preview.equipment')}{' '}
                    {service.type2Equipment
                      .map((e) => `${e.name} (${e.quantity})`)
                      .join('، ')}
                  </div>
                )}
              </div>
            )}
            {service.type3Amount > 0 && (
              <div>
                • {AMBULANCE_SERVICE_TYPES.type3.name}: {service.type3Amount}
                {service.type3Equipment.length > 0 && (
                  <div className="text-muted-foreground ms-3">
                    {t('preview.equipment')}{' '}
                    {service.type3Equipment
                      .map((e) => `${e.name} (${e.quantity})`)
                      .join('، ')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
      {withoutDriverServices.map((service, index) => (
        <div key={`without-driver-${index}`}>
          <div className="font-medium">
            {t('services.ambulanceWithoutDriverAlt')}
          </div>
          <div className="text-muted-foreground me-4 space-y-0.5 text-xs">
            {service.type1Amount > 0 && (
              <div>
                • {AMBULANCE_SERVICE_TYPES.type1.name}: {service.type1Amount}
                {service.type1Equipment.length > 0 && (
                  <div className="text-muted-foreground ms-3">
                    {t('preview.equipment')}{' '}
                    {service.type1Equipment
                      .map((e) => `${e.name} (${e.quantity})`)
                      .join('، ')}
                  </div>
                )}
              </div>
            )}
            {service.type2Amount > 0 && (
              <div>
                • {AMBULANCE_SERVICE_TYPES.type2.name}: {service.type2Amount}
                {service.type2Equipment.length > 0 && (
                  <div className="text-muted-foreground ms-3">
                    {t('preview.equipment')}{' '}
                    {service.type2Equipment
                      .map((e) => `${e.name} (${e.quantity})`)
                      .join('، ')}
                  </div>
                )}
              </div>
            )}
            {service.type3Amount > 0 && (
              <div>
                • {AMBULANCE_SERVICE_TYPES.type3.name}: {service.type3Amount}
                {service.type3Equipment.length > 0 && (
                  <div className="text-muted-foreground ms-3">
                    {t('preview.equipment')}{' '}
                    {service.type3Equipment
                      .map((e) => `${e.name} (${e.quantity})`)
                      .join('، ')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
      {ambulance4x4WithDriver.amount > 0 && (
        <div>
          <div className="font-medium">
            {ambulance4x4WithDriver.amount}{' '}
            {t('services.ambulance4x4WithDriver')}
          </div>
          {ambulance4x4WithDriver.equipment.length > 0 && (
            <div className="text-muted-foreground ms-3 text-xs">
              {t('preview.equipment')}{' '}
              {ambulance4x4WithDriver.equipment
                .map((e) => `${e.name} (${e.quantity})`)
                .join('، ')}
            </div>
          )}
        </div>
      )}
      {ambulance4x4WithoutDriver.amount > 0 && (
        <div>
          <div className="font-medium">
            {ambulance4x4WithoutDriver.amount}{' '}
            {t('services.ambulance4x4WithoutDriver')}
          </div>
          {ambulance4x4WithoutDriver.equipment.length > 0 && (
            <div className="text-muted-foreground ms-3 text-xs">
              {t('preview.equipment')}{' '}
              {ambulance4x4WithoutDriver.equipment
                .map((e) => `${e.name} (${e.quantity})`)
                .join('، ')}
            </div>
          )}
        </div>
      )}
      {mobileClinicBasic.amount > 0 && (
        <div>
          <div className="font-medium">
            {mobileClinicBasic.amount} {t('services.mobileClinicBasic')}
          </div>
          {mobileClinicBasic.equipment.length > 0 && (
            <div className="text-muted-foreground ms-3 text-xs">
              {t('preview.equipment')}{' '}
              {mobileClinicBasic.equipment
                .map((e) => `${e.name} (${e.quantity})`)
                .join('، ')}
            </div>
          )}
        </div>
      )}
      {mobileClinicAdvanced.amount > 0 && (
        <div>
          <div className="font-medium">
            {mobileClinicAdvanced.amount} {t('services.mobileClinicAdvanced')}
          </div>
          {mobileClinicAdvanced.equipment.length > 0 && (
            <div className="text-muted-foreground ms-3 text-xs">
              {t('preview.equipment')}{' '}
              {mobileClinicAdvanced.equipment
                .map((e) => `${e.name} (${e.quantity})`)
                .join('، ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MedicalStaffPreview({
  paramedicServices,
  nurseServices,
  doctorServices,
}: {
  paramedicServices: FormValues['paramedicServices'];
  nurseServices: FormValues['nurseServices'];
  doctorServices: FormValues['doctorServices'];
}) {
  const paramedicSpecialist = paramedicServices.find(
    (s) => s.type === 'SPECIALIST',
  );
  const paramedicTechnician = paramedicServices.find(
    (s) => s.type === 'TECHNICIAN',
  );
  const paramedicTotal =
    (paramedicSpecialist?.amount ?? 0) + (paramedicTechnician?.amount ?? 0);

  const nurseSpecialist = nurseServices.find((s) => s.type === 'SPECIALIST');
  const nurseTechnician = nurseServices.find((s) => s.type === 'TECHNICIAN');
  const nurseTotal =
    (nurseSpecialist?.amount ?? 0) + (nurseTechnician?.amount ?? 0);

  const doctorTotal = doctorServices.reduce((sum, s) => sum + s.amount, 0);

  const { t } = useTranslation();
  const hasStaff = paramedicTotal > 0 || nurseTotal > 0 || doctorTotal > 0;

  if (!hasStaff) {
    return (
      <div className="text-muted-foreground text-sm italic">
        {t('newRequest.noStaffSelected')}
      </div>
    );
  }

  return (
    <div className="space-y-2 text-sm">
      {paramedicTotal > 0 && (
        <div>
          <div className="font-medium">{t('preview.paramedics')}</div>
          <div className="text-muted-foreground me-4 space-y-0.5 text-xs">
            {paramedicSpecialist && paramedicSpecialist.amount > 0 && (
              <div>
                • {t('preview.specialist')} {paramedicSpecialist.amount}
              </div>
            )}
            {paramedicTechnician && paramedicTechnician.amount > 0 && (
              <div>
                • {t('preview.technician')} {paramedicTechnician.amount}
              </div>
            )}
          </div>
        </div>
      )}
      {nurseTotal > 0 && (
        <div>
          <div className="font-medium">{t('preview.nurses')}</div>
          <div className="text-muted-foreground me-4 space-y-0.5 text-xs">
            {nurseSpecialist && nurseSpecialist.amount > 0 && (
              <div>
                • {t('preview.specialist')} {nurseSpecialist.amount}
              </div>
            )}
            {nurseTechnician && nurseTechnician.amount > 0 && (
              <div>
                • {t('preview.technician')} {nurseTechnician.amount}
              </div>
            )}
          </div>
        </div>
      )}
      {doctorTotal > 0 && (
        <div>
          <div className="font-medium">{t('preview.doctorsLabel')}</div>
          <div className="text-muted-foreground me-4 space-y-0.5 text-xs">
            {doctorServices
              .filter((d) => d.amount > 0 && d.specialization)
              .map((d, i) => (
                <div key={i}>
                  • {d.specialization}: {d.amount}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MedicalEquipmentPreview({
  medicalEquipment,
}: {
  medicalEquipment: FormValues['medicalEquipment'];
}) {
  const filteredEquipment = medicalEquipment.filter(
    (e) => e.name.trim() && e.quantity > 0,
  );

  const { t } = useTranslation();

  if (filteredEquipment.length === 0) {
    return (
      <div className="text-muted-foreground text-sm italic">
        {t('newRequest.noEquipmentAdded')}
      </div>
    );
  }

  return (
    <div className="space-y-2 text-sm">
      <div>
        <div className="font-medium">{t('preview.medicalEquipment')}</div>
        <div className="text-muted-foreground me-4 space-y-0.5 text-xs">
          {filteredEquipment.map((equipment, i) => (
            <div key={i}>
              • {equipment.name} ({t('preview.quantityLabel')}{' '}
              {equipment.quantity})
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
